import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Lock } from 'lucide-react';
import { fetchAdminUsers, createAdminUser, updateAdminUser, deleteAdminUser } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const ROLES = [
  { value: 'superadmin', label: 'Super Admin', color: '#00C853' },
  { value: 'admin', label: 'Admin', color: '#4A9EFF' },
  { value: 'ops', label: 'Ops', color: '#FF6B35' },
  { value: 'gerente', label: 'Gerente', color: '#FFD600' },
  { value: 'responsable', label: 'Responsable', color: '#FF9800' },
  { value: 'viewer', label: 'Viewer', color: '#546E7A' },
  { value: 'global_viewer', label: 'Lectura Global', color: '#26C6DA' },
];

export function RoleBadge({ role }) {
  const r = ROLES.find((x) => x.value === role) || ROLES[ROLES.length - 1];
  return (
    <Badge
      className="text-[10px]"
      style={{ background: `${r.color}20`, color: r.color, border: `1px solid ${r.color}40` }}
    >
      {r.label}
    </Badge>
  );
}

function UserDialog({ open, onClose, user, onSave }) {
  const isNew = !user;
  const [form, setForm] = useState(user || { email: '', display_name: '', role: 'viewer', activo: true });
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);

  // UserDialog stays mounted across opens (Dialog only toggles visibility),
  // so `user` changing on a later "Editar" click never re-ran the useState
  // initializer above — the form kept whatever was set on the FIRST mount
  // (bug 18/09: Email/Nombre vacíos, Rol "Viewer" al editar cualquier
  // usuario tras haber abierto el diálogo una vez antes). Reset explicitly
  // whenever the dialog opens for a (possibly different) user.
  useEffect(() => {
    if (open) {
      setForm(user || { email: '', display_name: '', role: 'viewer', activo: true });
      setPassword('');
    }
  }, [open, user]);

  const handleSave = () => {
    if (!form.email) { toast.error('Email requerido'); return; }
    if (isNew && !password) { toast.error('Contraseña requerida para nuevo usuario'); return; }
    const payload = { ...form };
    if (password) payload.password = password;
    onSave(payload);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)' }} data-testid="user-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">{isNew ? 'Nuevo usuario' : 'Editar usuario'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1">
            <Label className="text-xs" style={{ color: 'var(--vz-text-secondary)' }}>Email</Label>
            <Input
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="border text-white"
              style={{ background: 'var(--vz-bg-elevated)', borderColor: 'var(--vz-border-subtle)' }}
              data-testid="user-email-input"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs" style={{ color: 'var(--vz-text-secondary)' }}>Nombre</Label>
            <Input
              value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              className="border text-white"
              style={{ background: 'var(--vz-bg-elevated)', borderColor: 'var(--vz-border-subtle)' }}
              data-testid="user-name-input"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs" style={{ color: 'var(--vz-text-secondary)' }}>Rol</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="border text-white" style={{ background: 'var(--vz-bg-elevated)', borderColor: 'var(--vz-border-subtle)' }} data-testid="user-role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs" style={{ color: 'var(--vz-text-secondary)' }}>
              Contraseña {!isNew && '(dejar vacío para no cambiar)'}
            </Label>
            <div className="relative">
              <Input
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="border text-white pr-9"
                style={{ background: 'var(--vz-bg-elevated)', borderColor: 'var(--vz-border-subtle)' }}
                data-testid="user-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPwd(!showPwd)}
                className="absolute right-2 top-1/2 -translate-y-1/2 opacity-50 hover:opacity-100"
              >
                <Lock size={13} style={{ color: 'var(--vz-text-muted)' }} />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              checked={form.activo}
              onCheckedChange={(v) => setForm({ ...form, activo: v })}
              data-testid="user-activo-switch"
            />
            <Label className="text-xs" style={{ color: 'var(--vz-text-secondary)' }}>Cuenta activa</Label>
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="ghost" className="flex-1" onClick={onClose} data-testid="user-cancel-button">Cancelar</Button>
            <Button
              className="flex-1"
              style={{ background: '#4A9EFF', color: '#06152E' }}
              onClick={handleSave}
              data-testid="user-save-button"
            >
              {isNew ? 'Crear usuario' : 'Guardar cambios'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function UsersTab() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);

  const { data: users = [], isLoading } = useQuery({ queryKey: ['admin-users'], queryFn: fetchAdminUsers });

  const createMut = useMutation({
    mutationFn: createAdminUser,
    onSuccess: () => { qc.invalidateQueries(['admin-users']); toast.success('Usuario creado'); },
    onError: () => toast.error('Error al crear usuario'),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, ...updates }) => updateAdminUser(id, updates),
    onSuccess: () => { qc.invalidateQueries(['admin-users']); toast.success('Usuario actualizado'); },
  });

  const deleteMut = useMutation({
    mutationFn: deleteAdminUser,
    onSuccess: () => { qc.invalidateQueries(['admin-users']); toast.success('Usuario desactivado'); },
  });

  const filtered = users.filter((u) =>
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.display_name?.toLowerCase().includes(search.toLowerCase())
  );

  const handleSave = (form) => {
    if (editUser?.id) {
      updateMut.mutate({ id: editUser.id, ...form });
    } else {
      createMut.mutate(form);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Input
          placeholder="Buscar por nombre o email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 h-8 text-xs border"
          style={{ background: 'var(--vz-bg-elevated)', borderColor: 'var(--vz-border-subtle)', color: 'white' }}
          data-testid="user-search-input"
        />
        <Button
          size="sm"
          className="h-8 gap-1 text-xs"
          style={{ background: '#00C853', color: '#06152E' }}
          onClick={() => { setEditUser(null); setDialogOpen(true); }}
          data-testid="add-user-button"
        >
          <Plus size={12} /> Nuevo usuario
        </Button>
      </div>

      <ScrollArea className="h-80">
        <Table>
          <TableHeader>
            <TableRow style={{ borderColor: 'var(--vz-border-subtle)' }}>
              {['Nombre', 'Email', 'Rol', 'Estado', 'Acciones'].map((h) => (
                <TableHead key={h} className="text-[10px] uppercase tracking-wider font-montserrat" style={{ color: 'var(--vz-text-muted)' }}>{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={i} style={{ borderColor: 'var(--vz-border-subtle)' }}>
                  {Array.from({ length: 5 }).map((_, j) => (
                    <TableCell key={j}><div className="h-3 rounded animate-pulse" style={{ background: 'var(--vz-bg-elevated)', width: '80%' }} /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-xs py-8" style={{ color: 'var(--vz-text-muted)' }}>
                  No hay usuarios. Crea el primero.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((u) => (
                <TableRow key={u.id || u.email} className="hover:bg-[#132860] transition-colors duration-200" style={{ borderColor: 'var(--vz-border-subtle)' }}>
                  <TableCell className="text-xs font-medium text-white">{u.display_name || '-'}</TableCell>
                  <TableCell className="text-xs" style={{ color: 'var(--vz-text-secondary)' }}>{u.email}</TableCell>
                  <TableCell><RoleBadge role={u.role} /></TableCell>
                  <TableCell>
                    <Badge className="text-[10px]"
                      style={{
                        background: u.activo ? 'rgba(0,200,83,0.12)' : 'rgba(255,82,82,0.12)',
                        color: u.activo ? '#00C853' : '#FF5252',
                        border: `1px solid ${u.activo ? 'rgba(0,200,83,0.3)' : 'rgba(255,82,82,0.3)'}`,
                      }}
                    >{u.activo ? 'Activo' : 'Inactivo'}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost" size="icon" className="h-6 w-6"
                        onClick={() => { setEditUser(u); setDialogOpen(true); }}
                        data-testid={`edit-user-${u.id}`}
                      >
                        <Pencil size={11} style={{ color: 'var(--vz-text-secondary)' }} />
                      </Button>
                      <Button
                        variant="ghost" size="icon" className="h-6 w-6"
                        onClick={() => deleteMut.mutate(u.id)}
                        data-testid={`delete-user-${u.id}`}
                      >
                        <Trash2 size={11} style={{ color: '#FF5252' }} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </ScrollArea>

      <UserDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        user={editUser}
        onSave={handleSave}
      />
    </div>
  );
}
